<template>
  <div class="relative py-16 md:py-20 overflow-hidden gallery-section">

    <!-- Section Header -->
    <div class="container mb-8">
      <div class="text-center gallery-header">
        <div class="inline-flex items-center gap-2 justify-center mb-3">
          <div class="h-px w-8 bg-orange-600"></div>
          <span class="text-orange-600 text-sm font-semibold uppercase tracking-widest">Gallery</span>
          <div class="h-px w-8 bg-orange-600"></div>
        </div>
        <h3 class="md:text-4xl text-3xl font-bold dark:text-white mb-3">
          Our <span class="text-orange-600">Instagram</span>
        </h3>
        <p class="text-slate-400 dark:text-slate-300 max-w-sm mx-auto text-sm">
          Follow us for beautiful moments from our palace
        </p>
      </div>
    </div>

    <!-- Swiper Gallery -->
    <div class="relative gallery-swiper-wrap">
      <swiper
        v-if="images && images.length > 0"
        :modules="modules"
        :slides-per-view="2"
        :space-between="8"
        :loop="true"
        :autoplay="{
          delay: 2800,
          disableOnInteraction: false,
        }"
        :breakpoints="{
          640: { slidesPerView: 3 },
          768: { slidesPerView: 4 },
          1024: { slidesPerView: 6 },
        }"
        class="instagram-swiper"
      >
        <swiper-slide
          v-for="(image, index) in images"
          :key="image.id"
        >
          <button
            @click="openLightbox(index)"
            class="gallery-thumb relative w-full aspect-square overflow-hidden rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 group"
          >
            <img
              :src="image.url"
              :alt="`Photo ${index + 1}`"
              class="w-full h-full object-cover transition-all duration-500 group-hover:scale-115"
            />

            <!-- Overlay -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-center pb-3">
              <div class="flex items-center gap-1 text-white text-xs font-medium translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"/>
                </svg>
                View
              </div>
            </div>
          </button>
        </swiper-slide>
      </swiper>

      <!-- Instagram Center Button -->
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10">
        <a
          href="https://www.instagram.com/mi_digital_works"
          target="_blank"
          rel="noopener noreferrer"
          class="pointer-events-auto instagram-btn inline-flex items-center justify-center w-14 h-14 rounded-full shadow-xl text-white transition-all duration-300 hover:scale-110"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
          </svg>
        </a>
      </div>
    </div>

    <!-- Lightbox -->
    <Teleport to="body">
      <Transition name="lightbox-fade">
        <div
          v-if="lightboxOpen"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
          @click="closeLightbox"
        >
          <div class="relative w-full max-w-4xl mx-auto px-4" @click.stop>

            <!-- Close btn -->
            <button
              @click="closeLightbox"
              class="absolute -top-12 right-0 text-white bg-white/10 hover:bg-white/25 rounded-full w-10 h-10 flex items-center justify-center transition-all duration-200 hover:scale-110 hover:rotate-90"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>

            <!-- Image -->
            <Transition name="img-slide" mode="out-in">
              <img
                :key="currentImageIndex"
                :src="images[currentImageIndex].url"
                :alt="`Photo ${currentImageIndex + 1}`"
                class="w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl mx-auto block"
              />
            </Transition>

            <!-- Nav buttons -->
            <button
              @click="goToPrevious"
              class="absolute left-0 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-orange-600 rounded-full w-12 h-12 flex items-center justify-center transition-all duration-200 hover:scale-110 -translate-x-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>

            <button
              @click="goToNext"
              class="absolute right-0 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-orange-600 rounded-full w-12 h-12 flex items-center justify-center transition-all duration-200 hover:scale-110 translate-x-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>

            <!-- Counter -->
            <div class="absolute -bottom-10 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full tracking-wider">
              {{ currentImageIndex + 1 }} / {{ images.length }}
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay } from 'swiper/modules'
import 'swiper/css'
import store from "@/store";

const modules = [Autoplay]
const images = computed(() => store.state.usersImages?.data)

const lightboxOpen = ref(false)
const currentImageIndex = ref(0)

const openLightbox = (index) => {
  currentImageIndex.value = index
  lightboxOpen.value = true
  document.body.style.overflow = 'hidden'
}

const closeLightbox = () => {
  lightboxOpen.value = false
  document.body.style.overflow = ''
}

const goToPrevious = () => {
  currentImageIndex.value = currentImageIndex.value === 0
    ? images.value.length - 1
    : currentImageIndex.value - 1
}

const goToNext = () => {
  currentImageIndex.value = currentImageIndex.value === images.value.length - 1
    ? 0
    : currentImageIndex.value + 1
}

const handleKeyDown = (e) => {
  if (!lightboxOpen.value) return
  if (e.key === 'ArrowLeft') goToPrevious()
  if (e.key === 'ArrowRight') goToNext()
  if (e.key === 'Escape') closeLightbox()
}

let observer;
onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)

  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('revealed');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15 });

  document.querySelectorAll('.gallery-header').forEach(el => observer.observe(el));
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
  if (observer) observer.disconnect();
  document.body.style.overflow = ''
})
</script>

<style scoped>
.instagram-swiper {
  width: 100%;
  padding: 0;
}

.gallery-thumb {
  transform: scale(1);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.gallery-thumb:hover {
  transform: scale(1.03);
  z-index: 2;
}

/* Instagram gradient button */
.instagram-btn {
  background: radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%);
  box-shadow: 0 8px 24px rgba(214, 36, 159, 0.35);
}
.instagram-btn:hover {
  box-shadow: 0 12px 32px rgba(214, 36, 159, 0.5);
}

/* Gallery header reveal */
.gallery-header {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}
.gallery-header.revealed {
  opacity: 1;
  transform: translateY(0);
}

/* Lightbox transition */
.lightbox-fade-enter-active { transition: opacity 0.3s ease; }
.lightbox-fade-leave-active { transition: opacity 0.25s ease; }
.lightbox-fade-enter-from, .lightbox-fade-leave-to { opacity: 0; }

/* Image slide transition in lightbox */
.img-slide-enter-active {
  animation: imgSlideIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.img-slide-leave-active {
  animation: imgSlideOut 0.2s ease forwards;
}
@keyframes imgSlideIn {
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
}
@keyframes imgSlideOut {
  from { opacity: 1; transform: scale(1); }
  to   { opacity: 0; transform: scale(1.05); }
}
</style>